import {
  AlternativeMaterialDetailQuery,
  SalesOrganizationsQuery,
  useAlternativeMaterialDetailQuery,
  useSalesOrganizationsQuery
} from "@saleor/graphql";
import { FormikProps, useFormik } from "formik";
import moment from "moment-timezone";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useParams } from "react-router";
import * as Yup from "yup";

import { ALT_MATERIAL_TYPE } from "./EditItem";

const validationSchema = Yup.object().shape({
  saleOrganization: Yup.string().required("โปรดระบุข้อมูล"),
  soldTo: Yup.string().required("โปรดระบุข้อมูล"),
  type: Yup.string().required("โปรดระบุข้อมูล"),
  materialOwn: Yup.string().required("โปรดระบุข้อมูล")
});
interface FormikValueInit {
  saleOrganization: string;
  soldTo: string;
  type: string;
  materialOwn: string;
  priority: any[];
}

const AddAlternatedContext = React.createContext<{
  formik: FormikProps<FormikValueInit>;
  handleOpenPopup: () => void;
  handleClosePopup: () => void;
  openConfirmPopup: boolean;
  listOrganization: SalesOrganizationsQuery;
  setMaterialOwnIdError: React.Dispatch<React.SetStateAction<string>>;
  materialOwnIdError: string;
  itemDetailData: AlternativeMaterialDetailQuery;
  onAddAlterrmatedMaterial?(props?: any);
}>(undefined);

export const AddAlternatedProvider: React.FC = ({ children }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [materialOwnIdError, setMaterialOwnIdError] = useState("");
  const firstRender = useRef(true);
  const param = useParams<{ id: string }>();

  const { data: itemDetailData } = useAlternativeMaterialDetailQuery({
    variables: {
      id: param?.id
    },
    fetchPolicy: "no-cache"
  });

  const formik = useFormik({
    initialValues: {
      saleOrganization: "",
      soldTo: "",
      type: "",
      materialOwn: "",
      priority: []
    },
    enableReinitialize: false,
    validateOnChange: isSubmitted,
    validateOnBlur: false,
    onSubmit(values) {
      const { priority } = values;
      const hasNoValue = priority.filter(p => {
        if (Object.values(p).filter(e => !e).length > 0) {
          return true;
        }
        return false;
      });
      if (hasNoValue.length) {
        return;
      }
    },
    validationSchema
  });
  useEffect(() => {
    if (itemDetailData?.alternativeMaterial && firstRender.current) {
      if (
        formik.values.saleOrganization &&
        formik.values.type &&
        formik.values.priority.length &&
        formik.values.materialOwn &&
        formik.values.soldTo
      ) {
        firstRender.current = false;
        return;
      }
      if (!formik.values.saleOrganization) {
        formik.setFieldValue(
          "saleOrganization",
          itemDetailData.alternativeMaterial.salesOrganization?.id
        );
      }
      if (!formik.values.type) {
        formik.setFieldValue(
          "type",
          itemDetailData?.alternativeMaterial.type ||
            ALT_MATERIAL_TYPE.GRADE_GRAM
        );
      }
      if (!formik.values.priority || !formik.values.priority.length) {
        const data = itemDetailData?.alternativeMaterial.materialsOs?.length
          ? itemDetailData?.alternativeMaterial.materialsOs.map((e, index) => ({
              materialOsId: e?.materialOs?.id,
              priority: index,
              displayText: e?.materialOs?.code,
              dia: e?.diameter,
              code: e?.materialOs?.code,
              id: moment().valueOf() + index,
              disabled: false
            }))
          : [
              {
                materialOsId: "",
                priority: 0,
                displayText: "",
                dia: "",
                code: "",
                id: moment().valueOf(),
                disabled: false
              }
            ];
        formik.setFieldValue("priority", data);
      }
      if (!formik.values.materialOwn) {
        formik.setFieldValue(
          "materialOwn",
          itemDetailData?.alternativeMaterial.materialOwn?.id
        );
      }
      if (!formik.values.soldTo) {
        formik.setFieldValue(
          "soldTo",
          itemDetailData?.alternativeMaterial.soldTo?.id
        );
      }
    }
  }, [itemDetailData?.alternativeMaterial, formik.values]);

  useEffect(() => {
    if (formik.dirty && !isSubmitted) {
      setIsSubmitted(true);
    }
  }, [formik?.dirty]);

  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);

  const handleOpenPopup = useCallback(() => {
    setOpenConfirmPopup(true);
  }, []);

  const handleClosePopup = useCallback(() => {
    setOpenConfirmPopup(false);
  }, []);

  const { data: listOrganization } = useSalesOrganizationsQuery();

  return (
    <AddAlternatedContext.Provider
      value={{
        formik,
        handleOpenPopup,
        handleClosePopup,
        openConfirmPopup,
        listOrganization,
        setMaterialOwnIdError,
        materialOwnIdError,
        itemDetailData
      }}
    >
      {children}
    </AddAlternatedContext.Provider>
  );
};

export const useAddAltermatedMaterial = () => {
  const {
    formik,
    handleOpenPopup,
    handleClosePopup,
    openConfirmPopup,
    listOrganization,
    setMaterialOwnIdError,
    materialOwnIdError,
    itemDetailData
  } = React.useContext(AddAlternatedContext);
  return useMemo(
    () => ({
      formik,
      handleOpenPopup,
      handleClosePopup,
      openConfirmPopup,
      listOrganization,
      setMaterialOwnIdError,
      materialOwnIdError,
      itemDetailData
    }),
    [
      formik,
      handleOpenPopup,
      handleClosePopup,
      openConfirmPopup,
      listOrganization,
      setMaterialOwnIdError,
      materialOwnIdError,
      itemDetailData
    ]
  );
};
